import { useDeferredValue, useEffect, useRef, useState } from "react";
import { Form, Link } from "react-router-dom";
import { useCart } from "../hooks/use-cart";
import { useTheme } from "../hooks/use-theme";
import { getJson } from "../lib/api";
import { productPath } from "../lib/catalog";
import { toDarkModeImageUrl, toSentenceCase } from "../lib/utils";
import { SearchIcon } from "./site-icons";

type Suggestion = {
  id: number;
  slug: string;
  name: string;
  category: string | null;
  image: string | null;
  inStock: boolean;
};

type SuggestResponse = {
  suggestions: Suggestion[];
};

export function SearchCommand() {
  const theme = useTheme();
  const { addOne, cartCounts } = useCart();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [addingId, setAddingId] = useState<number | null>(null);
  const deferredQuery = useDeferredValue(query.trim());

  useEffect(() => {
    let ignore = false;

    async function loadSuggestions() {
      if (deferredQuery.length < 2) {
        setSuggestions([]);
        return;
      }

      try {
        const result = await getJson<SuggestResponse>(
          `/api/search/suggest?q=${encodeURIComponent(deferredQuery)}`
        );
        if (!ignore) {
          setSuggestions(result.suggestions);
          setOpen(true);
        }
      } catch {
        if (!ignore) {
          setSuggestions([]);
        }
      }
    }

    void loadSuggestions();

    return () => {
      ignore = true;
    };
  }, [deferredQuery]);

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, []);

  async function handleAddToCart(productId: number) {
    setAddingId(productId);

    try {
      await addOne(productId);
    } finally {
      setAddingId(null);
    }
  }

  return (
    <div className="relative w-full min-w-0" ref={rootRef}>
      <Link className="glass-panel flex w-full min-w-0 items-center justify-between gap-3 rounded-full px-4 py-3 md:hidden" to="/shop/search">
        <span className="flex min-w-0 flex-1 items-center gap-3 text-sm font-semibold text-nv-ink">
          <SearchIcon />
          <span className="truncate">Search products</span>
        </span>
        <span className="shrink-0 text-xs font-bold uppercase tracking-[0.18em] text-nv-slate">Open</span>
      </Link>

      <Form action="/shop/search" className="glass-panel hidden min-w-0 items-center gap-3 rounded-full px-4 py-3 md:flex">
        <span className="text-nv-slate">Search</span>
        <input
          className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-nv-slate/70"
          name="q"
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="Try: arduino nano, esp32, ultrasonic sensor, relay"
          value={query}
        />
        <button className="brand-button shrink-0 rounded-full px-4 py-2 text-sm font-semibold text-white transition" type="submit">
          Go
        </button>
      </Form>
      {open && suggestions.length > 0 ? (
        <div className="glass-panel absolute inset-x-0 top-[calc(100%+0.75rem)] rounded-3xl p-3">
          <div className="mb-2 flex items-center justify-between px-2 text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
            <span>Top matches</span>
            <button className="font-semibold text-nv-electric" onClick={() => setOpen(false)} type="button">
              Close
            </button>
          </div>
          <div className="space-y-1">
            {suggestions.map((item) => (
              <div className="flex items-center justify-between gap-3 rounded-2xl px-3 py-3 transition hover:bg-black/3" key={item.id}>
                <Link className="flex min-w-0 flex-1 items-center gap-3" onClick={() => setOpen(false)} to={productPath(item.slug, item.id)}>
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded-2xl border border-black/5 bg-black/5">
                    {item.image ? (
                      <img
                        alt={toSentenceCase(item.name)}
                        className="h-full w-full object-cover"
                        loading="lazy"
                        src={theme === "dark" ? toDarkModeImageUrl(item.image) || item.image : item.image}
                      />
                    ) : (
                      <div className="grid h-full w-full place-items-center text-[10px] font-semibold text-nv-slate">
                        No image
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate font-medium">{toSentenceCase(item.name)}</p>
                    <p className="truncate text-xs text-nv-slate">
                      {item.inStock ? "In stock" : "Out of stock"} {item.category ? `- ${item.category}` : ""}
                    </p>
                  </div>
                </Link>
                <div className="flex items-center gap-2">
                  {cartCounts.get(item.id) ? (
                    <Link
                      className="relative inline-flex h-9 w-9 items-center justify-center rounded-full border border-nv-electric/15 bg-nv-electric/6 text-nv-electric"
                      onClick={() => setOpen(false)}
                      title="View cart"
                      to="/shop/cart"
                    >
                      <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                        <path d="M3 4h2l2.1 9.2a1 1 0 0 0 1 .8h8.9a1 1 0 0 0 1-.8L20 7H7.2" />
                        <circle cx="10" cy="19" r="1.5" />
                        <circle cx="17" cy="19" r="1.5" />
                      </svg>
                      <span className="absolute -right-1 -top-1 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-nv-accent px-1 text-[10px] font-bold leading-none text-white">
                        {cartCounts.get(item.id)}
                      </span>
                    </Link>
                  ) : null}
                  <button
                    className="brand-button inline-flex min-w-[94px] items-center justify-center rounded-full px-4 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!item.inStock || addingId === item.id}
                    onClick={() => void handleAddToCart(item.id)}
                    type="button"
                  >
                    {addingId === item.id ? "Adding..." : item.inStock ? "Add" : "Out"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
